import { useEffect, useState } from 'react';

import { whenLazyComponentsLoaded } from '#src/lib/tracked-lazy';

const toQuery = ({ minWidth, maxWidth }) =>
  [
    minWidth != null && `(min-width: ${minWidth}px)`,
    maxWidth != null && `(max-width: ${maxWidth}px)`,
  ]
    .filter(Boolean)
    .join(' and ');

const widthMatches = (width, { minWidth, maxWidth }) =>
  (minWidth == null || width >= minWidth) &&
  (maxWidth == null || width <= maxWidth);

const subscribe = (mql, listener) => {
  if (mql.addEventListener) {
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }
  mql.addListener(listener); // Safari < 14
  return () => mql.removeListener(listener);
};

/**
 * Renders its children only while the screen width is within
 * minWidth..maxWidth (inclusive), wrapped in `component`.
 *
 * The server and the first client render both use `fakeWidth` (the width
 * guessed from the user agent), so hydration always matches the server HTML.
 * The real width is applied once lazy components have loaded, and only if it
 * changes the result: an update earlier could reach a Suspense boundary that
 * is still hydrating and make React discard its server HTML (error #421).
 *
 * @param {object} props
 * @param {number} [props.minWidth] - Smallest matching width, in px.
 * @param {number} [props.maxWidth] - Largest matching width, in px.
 * @param {number} props.fakeWidth - Width to assume until mounted.
 * @param {*} [props.component] - Wrapper element, `div` by default.
 * @param {*} props.children - Content, or `(matches) => content`.
 * @returns {JSX.Element|null}
 */
export function MediaQuery({
  minWidth,
  maxWidth,
  fakeWidth,
  component: Component = 'div',
  children,
  ...props
}) {
  const [matches, setMatches] = useState(() =>
    widthMatches(fakeWidth, { minWidth, maxWidth })
  );

  useEffect(() => {
    const mql = window.matchMedia(toQuery({ minWidth, maxWidth }));
    let active = true;
    const update = () => active && setMatches(mql.matches);
    const unsubscribe = subscribe(mql, update);
    whenLazyComponentsLoaded().then(update);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [minWidth, maxWidth]);

  if (typeof children === 'function') return children(matches);
  if (!matches) return null;
  return <Component {...props}>{children}</Component>;
}
